import { Cell, CellKind, Grid } from "@highmarch/domain";
import { sketchRoad } from "../render/roadSketch";

/**
 * The level-load war map (ui/levelLoading.ts; owner 2026-10-05: "the road builds itself"): the level's ground inked on
 * the parchment — woods and rocks where it is blocked, the gorge hatched — and a road drawing itself from the enemy
 * portal toward the fortress as the level's files come in, towers popping up beside it as it passes, raiders' marks
 * marching down what is drawn and the portal glowing. Progress IS the road: when it reaches the gate, the level is in.
 *
 * The road is a sketch (render/roadSketch.ts): in play the player lays their own with road cards, so this is the
 * kind of road that ground takes, the same one every time for that level. Canvas 2D, a few dozen strokes a frame.
 * `calm` (Reduced effects / reduced motion): the road follows the load without the pops, the marching or the glow.
 */
export interface WarMapInput {
  readonly grid: Grid;
  /** Seeds the sketch: the level number. */
  readonly seed: number;
  /** Icons for the towers that pop up along the road (owned towers), the fortress and nothing else. */
  readonly towerIcons: readonly string[];
  readonly fortressIcon: string | null;
  readonly calm: boolean;
}

/** Where the drawn road is, 0..1, given the load's own progress. Pure, for the tests. */
export function paceRoad(shown: number, target: number, dt: number, done: boolean): number {
  if (done) return Math.min(1, shown + Math.max(dt * 1.8, (1 - shown) * dt * 7));
  const t = Math.max(0, Math.min(1, target));
  // A load that stalls (one big file, a slow phone building) never leaves the road standing still, and never shows it
  // finished: it creeps on toward a ceiling a little ahead of the real progress, below the gate.
  const ceiling = Math.min(WarMapPace.MaxBeforeDone, t + (1 - t) * WarMapPace.Lead);
  let s = shown;
  if (s < t) s += (t - s) * Math.min(1, dt * WarMapPace.CatchUp) + dt * WarMapPace.MinSpeed;
  else s += (ceiling - s) * Math.min(1, dt * WarMapPace.Creep);
  return Math.max(shown, Math.min(s, ceiling));
}

export const WarMapPace = {
  /** The road never reaches the gate before the load is done. */
  MaxBeforeDone: 0.96,
  /** How far ahead of the real progress the creep may run, as a share of what is left. */
  Lead: 0.3,
  /** Per second: how fast the road closes on the real progress, and how fast it creeps when it has caught up. */
  CatchUp: 3,
  Creep: 0.18,
  /** Cells' worth a second the road always gains while behind (as a fraction of the road). */
  MinSpeed: 0.03,
} as const;

interface Spot { readonly at: number; readonly x: number; readonly y: number; readonly icon: number }

const INK = "58, 36, 16";

export class WarMap {
  readonly canvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D;
  private readonly road: { x: number; y: number; bridge: boolean }[];
  private readonly spots: Spot[] = [];
  private readonly icons: HTMLImageElement[];
  private readonly fortress: HTMLImageElement | null;
  private readonly fortressAt: { x: number; y: number };
  private readonly rotate: boolean;
  private readonly cols: number;
  private readonly rows: number;
  private cs = 16;
  private shown = 0;
  private time = 0;
  private finishedAt = -1;
  private readonly popped = new Map<number, number>();

  constructor(private readonly input: WarMapInput) {
    this.ctx = this.canvas.getContext("2d")!;
    const g = input.grid;
    this.rotate = g.height > g.width;                         // tall maps lie on their side: the card is wide
    this.cols = this.rotate ? g.height : g.width;
    this.rows = this.rotate ? g.width : g.height;
    const sketch = sketchRoad(g, input.seed * 7919);
    // portal … gate, then into the fortress
    const cells = [...sketch.cells].reverse();
    const fp = g.footprint;
    this.fortressAt = { x: fp.reduce((a, c) => a + c.x, 0) / fp.length, y: fp.reduce((a, c) => a + c.y, 0) / fp.length };
    this.road = cells.map((c, i) => ({ x: c.x, y: c.y, bridge: sketch.bridge[sketch.cells.length - 1 - i] }));
    this.road.push({ x: g.base.x, y: g.base.y, bridge: false });
    // towers beside the road, on open ground, over its fortress half (they pop as the road passes them)
    const used = new Set(cells.map((c) => `${c.x},${c.y}`));
    const n = this.road.length;
    const fractions = [0.38, 0.52, 0.66, 0.8];
    fractions.forEach((f, k) => {
      if (input.towerIcons.length === 0) return;
      const i = Math.round(f * (n - 2));
      const c = this.road[i];
      for (const [dx, dy] of k % 2 ? [[1, 0], [0, 1], [-1, 0], [0, -1]] : [[-1, 0], [0, -1], [1, 0], [0, 1]]) {
        const s = new Cell(c.x + dx, c.y + dy);
        if (!g.inBounds(s) || g.kindAt(s) !== CellKind.Empty || used.has(`${s.x},${s.y}`)) continue;
        if (this.spots.some((p) => Math.abs(p.x - s.x) + Math.abs(p.y - s.y) < 2)) continue;
        used.add(`${s.x},${s.y}`);
        this.spots.push({ at: i / (n - 1), x: s.x, y: s.y, icon: k % input.towerIcons.length });
        break;
      }
    });
    const img = (src: string) => { const im = new Image(); im.decoding = "async"; im.src = src; return im; };
    this.icons = input.towerIcons.map(img);
    this.fortress = input.fortressIcon ? img(input.fortressIcon) : null;
  }

  /** Fits the map to a box (CSS px) and sizes the canvas for the screen's pixel density. */
  fit(maxW: number, maxH: number): void {
    this.cs = Math.max(8, Math.floor(Math.min(maxW / this.cols, maxH / this.rows)));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = this.cols * this.cs, h = this.rows * this.cs;
    this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = `${w}px`; this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** How far the road is drawn (0..1). */
  get progress(): number { return this.shown; }
  /** The road has reached the gate (after `done`). */
  get arrived(): boolean { return this.shown >= 1; }

  /** Advances the road toward `target` (the load's 0..1) and draws a frame. */
  step(dt: number, target: number, done: boolean): void {
    this.time += dt;
    this.shown = this.input.calm ? Math.max(this.shown, done ? 1 : Math.min(WarMapPace.MaxBeforeDone, target)) : paceRoad(this.shown, target, dt, done);
    if (this.shown >= 1 && this.finishedAt < 0) this.finishedAt = this.time;
    this.draw();
  }

  // ------------------------------------------------------------------------------------------------- drawing

  private px(x: number, y: number): [number, number] {
    const g = this.input.grid, cs = this.cs;
    return this.rotate ? [(y + 0.5) * cs, (g.width - x - 0.5) * cs] : [(x + 0.5) * cs, (g.height - y - 0.5) * cs];
  }

  private draw(): void {
    const c = this.ctx, g = this.input.grid, cs = this.cs;
    const W = this.cols * cs, H = this.rows * cs;
    c.clearRect(0, 0, W, H);
    // faint survey grid
    c.strokeStyle = `rgba(${INK}, 0.07)`; c.lineWidth = 1;
    c.beginPath();
    for (let i = 1; i < this.cols; i++) { c.moveTo(i * cs + 0.5, 0); c.lineTo(i * cs + 0.5, H); }
    for (let j = 1; j < this.rows; j++) { c.moveTo(0, j * cs + 0.5); c.lineTo(W, j * cs + 0.5); }
    c.stroke();
    // the ground: gorge hatched, woods and rocks inked
    for (let i = 0; i < g.cellCount; i++) {
      const cell = g.cellAt(i), kind = g.kindAt(cell);
      if (kind === CellKind.Chasm) this.gorge(cell);
      else if (kind === CellKind.Blocked) this.wood(cell, i);
    }
    this.drawRoad();
    this.portal();
    this.towers();
    this.fort();
    // the map's double rule
    c.strokeStyle = `rgba(${INK}, 0.55)`; c.lineWidth = 1.5; c.strokeRect(1, 1, W - 2, H - 2);
    c.strokeStyle = `rgba(${INK}, 0.25)`; c.lineWidth = 1; c.strokeRect(4.5, 4.5, W - 9, H - 9);
  }

  /** A gorge: a dark shaded floor, a crack down it, and cliff hachures along every edge that meets the ground. */
  private gorge(cell: Cell): void {
    const c = this.ctx, cs = this.cs, g = this.input.grid, [x, y] = this.px(cell.x, cell.y);
    c.fillStyle = `rgba(${INK}, 0.26)`;
    c.fillRect(x - cs / 2, y - cs / 2, cs, cs);
    c.strokeStyle = `rgba(${INK}, 0.55)`; c.lineWidth = 1;
    c.beginPath();
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const n = new Cell(cell.x + dx, cell.y + dy);
      if (!g.inBounds(n) || g.kindAt(n) === CellKind.Chasm) continue;
      // the edge toward that neighbour, in canvas space, with short ticks hanging into the gorge
      const [nx, ny] = this.px(n.x, n.y), ex = (nx - x) / cs, ey = (ny - y) / cs;
      const mx = x + ex * cs / 2, my = y + ey * cs / 2, tx = -ey, ty = ex;
      for (let k = -2; k <= 2; k++) { const o = k * cs * 0.2; c.moveTo(mx + tx * o, my + ty * o); c.lineTo(mx + tx * o - ex * cs * 0.22, my + ty * o - ey * cs * 0.22); }
    }
    c.stroke();
  }

  private wood(cell: Cell, i: number): void {
    const c = this.ctx, cs = this.cs, [x, y] = this.px(cell.x, cell.y);
    const h = (i * 2654435761) >>> 0;
    c.strokeStyle = `rgba(${INK}, 0.6)`; c.lineWidth = 1.1;
    if (h % 3 === 0) {
      // a rock: a peak with a shaded flank
      c.fillStyle = `rgba(${INK}, 0.18)`;
      c.beginPath(); c.moveTo(x - cs * 0.36, y + cs * 0.26); c.lineTo(x - cs * 0.04, y - cs * 0.3); c.lineTo(x + cs * 0.38, y + cs * 0.26); c.closePath(); c.fill(); c.stroke();
    } else {
      // a tree: a round crown on a stem, two to a cell
      for (const [ox, oy, r] of [[-0.18, 0.04, 0.22], [0.2, -0.1, 0.18]] as const) {
        c.fillStyle = `rgba(74, 98, 52, ${0.32 + (h % 5) * 0.04})`;
        c.beginPath(); c.arc(x + ox * cs, y + oy * cs, r * cs, 0, Math.PI * 2); c.fill(); c.stroke();
        c.beginPath(); c.moveTo(x + ox * cs, y + (oy + r) * cs); c.lineTo(x + ox * cs, y + (oy + r + 0.14) * cs); c.stroke();
      }
    }
  }

  /** The road up to `shown`: a tan bed, an inked dashed track over it, planks on the bridges, the pen at its head. */
  private drawRoad(): void {
    const c = this.ctx, cs = this.cs, pts = this.road.map((p) => this.px(p.x, p.y));
    const segs = pts.length - 1, len = this.shown * segs;
    if (len <= 0) return;
    const head = this.pointAt(len);
    const trace = () => {
      c.beginPath(); c.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i <= Math.floor(len); i++) c.lineTo(pts[i][0], pts[i][1]);
      c.lineTo(head[0], head[1]);
    };
    c.lineCap = "round"; c.lineJoin = "round";
    c.strokeStyle = "rgba(196, 156, 96, 0.95)"; c.lineWidth = cs * 0.7; trace(); c.stroke();
    c.strokeStyle = `rgba(${INK}, 0.5)`; c.lineWidth = 1.2; c.setLineDash([]);
    // the bed's inked edges (a second, thinner pass reads as two lines on paper)
    c.strokeStyle = `rgba(${INK}, 0.85)`; c.lineWidth = 1.8; c.setLineDash([cs * 0.3, cs * 0.22]); trace(); c.stroke(); c.setLineDash([]);
    // planks across the bridged gorge
    c.strokeStyle = "rgba(110, 70, 30, 0.9)"; c.lineWidth = 1.4;
    for (let i = 0; i < Math.min(segs + 1, Math.floor(len) + 1); i++) {
      if (!this.road[i].bridge) continue;
      const [x, y] = pts[i], n = pts[Math.min(segs, i + 1)], p = pts[Math.max(0, i - 1)];
      const dx = n[0] - p[0], dy = n[1] - p[1], l = Math.hypot(dx, dy) || 1, ux = -dy / l, uy = dx / l, ax = dx / l, ay = dy / l;
      c.beginPath();
      for (const k of [-0.3, 0, 0.3]) { c.moveTo(x + ax * k * cs - ux * cs * 0.38, y + ay * k * cs - uy * cs * 0.38); c.lineTo(x + ax * k * cs + ux * cs * 0.38, y + ay * k * cs + uy * cs * 0.38); }
      c.stroke();
    }
    if (!this.input.calm) this.march(len);
    if (this.shown < 1) {
      // the pen: an ink drop with a warm glow, breathing
      const r = cs * (0.2 + 0.05 * Math.sin(this.time * 9));
      const glow = c.createRadialGradient(head[0], head[1], 0, head[0], head[1], cs * 0.9);
      glow.addColorStop(0, "rgba(255, 214, 120, 0.75)"); glow.addColorStop(1, "rgba(255, 214, 120, 0)");
      c.fillStyle = glow; c.beginPath(); c.arc(head[0], head[1], cs * 0.9, 0, Math.PI * 2); c.fill();
      c.fillStyle = `rgb(${INK})`; c.beginPath(); c.arc(head[0], head[1], r, 0, Math.PI * 2); c.fill();
    }
  }

  /** Raiders' marks: red chevrons walking down the drawn road from the portal, round and round. */
  private march(len: number): void {
    const c = this.ctx, cs = this.cs, gap = 2.6, speed = 1.1;
    c.fillStyle = "rgba(179, 38, 26, 0.9)";
    for (let k = 0; k < 6; k++) {
      const d = (this.time * speed + k * gap) % (Math.max(len, 0.01) + gap) - gap * 0.2;
      if (d < 0 || d > len - 0.35) continue;
      const [x, y] = this.pointAt(d), [nx, ny] = this.pointAt(Math.min(len, d + 0.2));
      const a = Math.atan2(ny - y, nx - x);
      c.save(); c.translate(x, y); c.rotate(a);
      c.beginPath(); c.moveTo(cs * 0.2, 0); c.lineTo(-cs * 0.14, -cs * 0.16); c.lineTo(-cs * 0.04, 0); c.lineTo(-cs * 0.14, cs * 0.16); c.closePath(); c.fill();
      c.restore();
    }
  }

  private pointAt(d: number): [number, number] {
    const pts = this.road, i = Math.min(pts.length - 2, Math.floor(d)), f = Math.min(1, d - i);
    const [ax, ay] = this.px(pts[i].x, pts[i].y), [bx, by] = this.px(pts[i + 1].x, pts[i + 1].y);
    return [ax + (bx - ax) * f, ay + (by - ay) * f];
  }

  /** The enemy portal at the road's far end: a violet glow and a turning ring. */
  private portal(): void {
    const c = this.ctx, cs = this.cs, p = this.road[0], [x, y] = this.px(p.x, p.y);
    const pulse = this.input.calm ? 0.5 : 0.5 + 0.5 * Math.sin(this.time * 3.2);
    const R = cs * (1.05 + 0.25 * pulse);
    const g = c.createRadialGradient(x, y, 0, x, y, R);
    g.addColorStop(0, "rgba(232, 190, 255, 0.95)"); g.addColorStop(0.35, "rgba(150, 70, 220, 0.6)"); g.addColorStop(1, "rgba(110, 40, 180, 0)");
    c.fillStyle = g; c.beginPath(); c.arc(x, y, R, 0, Math.PI * 2); c.fill();
    c.strokeStyle = "rgba(90, 30, 140, 0.85)"; c.lineWidth = 1.6;
    const rot = this.input.calm ? 0 : this.time * 1.8;
    for (let k = 0; k < 3; k++) { c.beginPath(); c.arc(x, y, cs * 0.42, rot + k * 2.09, rot + k * 2.09 + 1.3); c.stroke(); }
  }

  /** Towers beside the road, each popping up (a little overshoot) as the road passes it. */
  private towers(): void {
    const c = this.ctx, cs = this.cs;
    this.spots.forEach((s, i) => {
      if (this.shown < s.at) return;
      if (!this.popped.has(i)) this.popped.set(i, this.time);
      const t = this.input.calm ? 1 : Math.min(1, (this.time - this.popped.get(i)!) / 0.42);
      const k = t >= 1 ? 1 : 1 - Math.pow(1 - t, 3) * Math.cos(t * 7.5);   // springy pop
      const im = this.icons[s.icon], [x, y] = this.px(s.x, s.y), size = cs * 2.1 * k;
      c.fillStyle = `rgba(${INK}, ${0.22 * k})`;
      c.beginPath(); c.ellipse(x, y + cs * 0.32, cs * 0.42 * k, cs * 0.16 * k, 0, 0, Math.PI * 2); c.fill();
      if (im?.complete && im.naturalWidth > 0) c.drawImage(im, x - size / 2, y + cs * 0.38 - size, size, size);
      else { c.fillStyle = "rgba(107, 68, 35, 0.9)"; c.fillRect(x - cs * 0.22 * k, y - cs * 0.5 * k, cs * 0.44 * k, cs * 0.8 * k); }
    });
  }

  /** The fortress at the gate; when the road arrives, it gives a hop and a gold flash. */
  private fort(): void {
    const c = this.ctx, cs = this.cs, [x, y] = this.px(this.fortressAt.x, this.fortressAt.y);
    const since = this.finishedAt < 0 ? -1 : this.time - this.finishedAt;
    const hop = since >= 0 && since < 0.5 && !this.input.calm ? Math.sin((since / 0.5) * Math.PI) : 0;
    if (since >= 0 && !this.input.calm) {
      const R = cs * (1.2 + since * 4);
      const g = c.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, `rgba(255, 215, 92, ${Math.max(0, 0.8 - since * 1.4)})`); g.addColorStop(1, "rgba(255, 215, 92, 0)");
      c.fillStyle = g; c.beginPath(); c.arc(x, y, R, 0, Math.PI * 2); c.fill();
    }
    const size = cs * 2.6 * (1 + 0.12 * hop);
    c.fillStyle = `rgba(${INK}, 0.25)`;
    c.beginPath(); c.ellipse(x, y + cs * 0.55, cs * 0.95, cs * 0.3, 0, 0, Math.PI * 2); c.fill();
    const im = this.fortress;
    if (im?.complete && im.naturalWidth > 0) c.drawImage(im, x - size / 2, y + cs * 0.7 - size - hop * cs * 0.3, size, size);
    else { c.fillStyle = "rgba(107, 68, 35, 0.95)"; c.fillRect(x - cs * 0.8, y - cs * 0.6, cs * 1.6, cs * 1.2); }
  }
}
